/* Breakz — block reveals, played once as a block comes into view.

   Sections mark what should appear with `.bx-in` and a direction:
   `--up` (the default), `--fade`, `--left`, `--right`, `--zoom`. This script
   is what moves them.

   WHY IT IS TIME-BASED. Until 14.09.2026 the reveals ran on a scroll
   timeline (`animation-timeline: view()`): the animation was scrubbed by the
   scroll position, 24 px over a stretch of scrolling. On a phone that read as
   almost nothing, and the site looked static; on an iPhone 14 it also
   stuttered, because Safari re-rasters a scroll-linked layer on every frame.
   Here a block waits off screen and, once it enters, its animation plays
   through in under a second — the behaviour of AOS
   (michalsnik.github.io/aos), without shipping the library for it.

   PHONE AND DESKTOP ARE TUNED APART. On a phone two to four blocks enter at a
   time, and a 900 ms reveal with a 90 ms cascade reads as motion. On a
   desktop the category grid puts eleven tiles on screen at once: the last
   one was fully there 1.4 s after the grid arrived, which Kirill read as the
   section still loading and the page hanging (14.09.2026) — while frame
   times in a trace stayed at 60 fps. From 990 px the reveal is shorter
   (600 ms), travels less (32 px), cascades faster (45 ms, capped at 225 ms)
   and starts the moment a block shows, so it has mostly landed by the time
   the eye gets there.

   NOTHING STAYS HIDDEN. The hidden state is set by this script, and only on
   blocks that are off screen at the moment it runs:
   - without JavaScript, or if this file fails to load, every block is there;
   - a block already on screen is left exactly as it was painted, no flash;
   - a row that scrolls sideways reveals as one. On a phone the product and
     video rows do, and their cards past the right edge can never enter the
     screen by scrolling down — they would wait, hidden, until swiped to;
   - a card inside a closed tab reveals when the tab opens and it takes space;
   - under prefers-reduced-motion: reduce the script does nothing and every
     block is static — a separate implementation, not a slower one.
   Checked on the sandbox on 14.09.2026: after a full scroll no visible block
   was left hidden, at 390 px in Chromium and WebKit and at 1440 px.

   WHY WEB ANIMATIONS AND NOT A CSS TRANSITION. Many of these blocks carry
   hover and press transitions of their own under an id selector, which
   would beat a shared class and cancel the reveal. An animation started from
   script sits above them while it runs and then hands the element back
   untouched. Only opacity and transform move, so layout never shifts.

   The observer uses the viewport as its root on every width. On desktop
   Horizon scrolls `.page-wrapper` rather than the window, and an implicit
   root still sees blocks move inside it — measured, not assumed. */

(() => {
  if (window.BreakzReveal) return;
  // replay(el) plays a block's reveal again, with the same timing the page
  // uses. The design system page calls it, so its motion row shows this
  // script's animation rather than a copy. It does nothing under reduced motion.
  window.BreakzReveal = { replay() {} };

  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window) || !Element.prototype.animate) return;

  const narrow = matchMedia('(max-width: 749px)');
  const wide = matchMedia('(min-width: 990px)');
  const EASE_OUT = 'cubic-bezier(.16, 1, .3, 1)';
  const seen = new WeakSet();

  // Timing for the current width: phone, tablet, desktop.
  const tune = () => {
    if (wide.matches) return { shift: 32, side: 40, move: 600, fade: 500, zoom: 600, step: 45, cap: 225 };
    if (narrow.matches) return { shift: 40, side: 32, move: 900, fade: 700, zoom: 800, step: 90, cap: 540 };
    return { shift: 48, side: 48, move: 800, fade: 600, zoom: 700, step: 70, cap: 420 };
  };

  // The starting pose for each direction. There is no end keyframe: the
  // animation lands on the element's own computed style, whatever it is.
  const effect = (el, t) => {
    const is = (name) => el.classList.contains('bx-in--' + name);

    if (is('fade')) return { from: { opacity: 0 }, duration: t.fade, easing: 'cubic-bezier(.22, .61, .36, 1)' };
    if (is('left')) return { from: { opacity: 0, transform: `translate3d(${-t.side}px, 0, 0)` }, duration: t.move, easing: EASE_OUT };
    if (is('right')) return { from: { opacity: 0, transform: `translate3d(${t.side}px, 0, 0)` }, duration: t.move, easing: EASE_OUT };
    // A slight overshoot, the "ease out back" of AOS's zoom presets.
    if (is('zoom')) return { from: { opacity: 0, transform: 'scale(.92)' }, duration: t.zoom, easing: 'cubic-bezier(.34, 1.4, .64, 1)' };
    return { from: { opacity: 0, transform: `translate3d(0, ${t.shift}px, 0)` }, duration: t.move, easing: EASE_OUT };
  };

  const play = (el, delay, t) => {
    if (!el.classList.contains('bx-pending')) return;
    const { from, duration, easing } = effect(el, t);
    // Removing the hidden class and starting the animation in the same task
    // means the next frame already shows the starting pose: no flash between.
    el.classList.remove('bx-pending');
    el.animate([{ ...from, offset: 0 }], { duration, easing, delay, fill: 'backwards' });
  };

  window.BreakzReveal.replay = (el) => {
    if (!el) return;
    el.getAnimations().forEach((animation) => animation.cancel());
    const { from, duration, easing } = effect(el, tune());
    el.animate([{ ...from, offset: 0 }], { duration, easing });
  };

  const scrollsSideways = (el) => {
    if (!el) return false;
    const overflow = getComputedStyle(el).overflowX;
    return (overflow === 'auto' || overflow === 'scroll') && el.scrollWidth > el.clientWidth + 1;
  };

  const observer = new IntersectionObserver(
    (entries) => {
      const t = tune();
      const batch = new Set();
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        batch.add(entry.target);
        const row = entry.target.parentElement;
        if (scrollsSideways(row)) {
          row.querySelectorAll(':scope > .bx-in.bx-pending').forEach((el) => batch.add(el));
        }
      }
      [...batch]
        .map((el) => [el, el.getBoundingClientRect()])
        .sort(([, a], [, b]) => Math.round(a.top - b.top) || a.left - b.left)
        .forEach(([el], index) => {
          observer.unobserve(el);
          play(el, Math.min(index * t.step, t.cap), t);
        });
    },
    // A phone starts a block once its top is past the bottom 8% of the
    // screen; a desktop starts it as soon as it shows.
    { rootMargin: wide.matches ? '0px' : '0px 0px -8% 0px' }
  );

  const arm = (root) => {
    root.querySelectorAll('.bx-in').forEach((el) => {
      if (seen.has(el)) return;
      seen.add(el);
      const box = el.getBoundingClientRect();
      if (box.height > 0 && box.bottom > 0 && box.top < innerHeight) return;
      el.classList.add('bx-pending');
      observer.observe(el);
    });
  };

  arm(document);

  // A block waiting for its reveal is transparent, and keyboard focus does not
  // wait for the observer: tabbing onto a link inside one left the focus on
  // something nobody could see (measured 14.09.2026 on the category tiles,
  // opacity 0 while focused). Focus plays the block's reveal at once.
  document.addEventListener('focusin', (event) => {
    const el = event.target instanceof Element ? event.target.closest('.bx-in.bx-pending') : null;
    if (!el) return;
    observer.unobserve(el);
    play(el, 0, tune());
  });

  // The theme editor re-renders a section in place when a setting changes.
  document.addEventListener('shopify:section:load', (event) => arm(event.target));
})();
